import { useEffect } from "react";
import { SITE } from "../data/site";

const normalizePathname = (pathname) => {
  if (!pathname) {
    return "/";
  }

  const cleanedPath = pathname.replace(/\/+$/, "");

  return cleanedPath || "/";
};

const normalizeCanonicalUrl = (value) => {
  if (!value) {
    return "";
  }

  try {
    const resolvedUrl = new URL(value, SITE.baseUrl);
    resolvedUrl.pathname = normalizePathname(resolvedUrl.pathname);
    resolvedUrl.search = "";
    resolvedUrl.hash = "";

    return resolvedUrl.toString();
  } catch {
    return value;
  }
};

/**
 * Keeps document title and description in sync with page content.
 */
export default function usePageMeta({
  title,
  description,
  canonicalUrl,
  ogImageUrl,
  ogType = "website",
  noindex = false,
}) {
  useEffect(() => {
    const upsertMeta = (attrName, attrValue, content) => {
      if (!content) {
        return;
      }

      let meta = document.querySelector(`meta[${attrName}="${attrValue}"]`);

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attrName, attrValue);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    const upsertLink = (rel, href) => {
      if (!href) {
        return;
      }

      let link = document.querySelector(`link[rel="${rel}"]`);

      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }

      link.setAttribute("href", href);
    };

    if (title) {
      document.title = title;
    }

    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');

      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.setAttribute("name", "description");
        document.head.appendChild(metaDescription);
      }

      metaDescription.setAttribute("content", description);
    }

    const resolvedCanonicalUrl = normalizeCanonicalUrl(
      canonicalUrl ||
        (typeof window !== "undefined" && window.location
          ? `${SITE.baseUrl}${window.location.pathname}`
          : "")
    );

    upsertLink("canonical", resolvedCanonicalUrl);

    const resolvedOgImageUrl = ogImageUrl || SITE.defaultOgImage;

    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:url", resolvedCanonicalUrl);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:image", resolvedOgImageUrl);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", resolvedOgImageUrl);

    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
  }, [canonicalUrl, description, noindex, ogImageUrl, ogType, title]);
}
